// src/components/PatientLayout.jsx
import React, { useState, useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import logo from '../assets/tu_logo.png';

import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import InstagramIcon from '@mui/icons-material/Instagram';

import { useThemeMode } from '../context/ThemeContext';
import SessionTimeout from './SessionTimeout';

const drawerWidth = 240;        // Igual al MainLayout
const base = '/patient';        // Prefijo rutas del portal de Paciente

const navItems = [
  { to: `${base}/dashboard`, label: 'Inicio',           icon: <DashboardIcon /> },
  { to: `${base}/appointments`, label: 'Mis Citas',     icon: <CalendarMonthIcon /> },
  { to: `${base}/medical-records`, label: 'Historial Médico', icon: <DescriptionIcon /> },
  { to: `${base}/profile`, label: 'Mi Perfil',          icon: <PersonIcon /> },
];

const PatientLayout = () => {
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleLogout = async () => {
    try {
      await axiosInstance.post('/patients/logout/').catch(() => {});
    } finally {
      localStorage.removeItem('patientToken');
      localStorage.removeItem('guestToken');
      localStorage.removeItem('isGuest');
      toast.info('Has cerrado la sesión.');
      navigate('/patient/login');
    }
  };

  // Estilos de navegación iguales al MainLayout
  const navItemStyles = {
    py: 1.2,
    px: 2.5,
    mb: 1,
    borderRadius: theme.shape.borderRadius,
    '&.Mui-selected': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.light, 0.16)
          : alpha(theme.palette.primary.main, 0.12),
      '&:hover': {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.light, 0.24)
            : alpha(theme.palette.primary.main, 0.16),
      },
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: 'primary.main',
        fontWeight: '600',
      },
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  };

  // Carga de perfil para el Chip (nombre y foto)
  useEffect(() => {
    let mounted = true;
    setLoadingProfile(true);
    axiosInstance.get('/patients/profile/')
      .then((res) => {
        if (!mounted) return;
        setProfile(res.data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setProfile(null);
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingProfile(false);
      });
    return () => { mounted = false; };
  }, []);


  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
      }}
    >
      {/* Encabezado con logo y subtítulo */}
      <Box>
        <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
          <RouterLink
            to="/patient/dashboard"
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <img src={logo} alt="Logo Medify" style={{ height: 40, marginRight: 12 }} />
            <Typography variant="h6" fontWeight="bold">
              MEDIFY
            </Typography>
          </RouterLink>
        </Toolbar>

        {/* Línea debajo del header */}
        <Divider />

        {/* Subtítulo "Portal del Paciente" */}
        <Box sx={{ px: 3, pt: 2, pb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.2 }}
          >
            Portal del Paciente
          </Typography>
        </Box>

        {/* NUEVO: línea divisora después del subtítulo */}
        <Divider sx={{ mx: 2, my: 1 }} />
      </Box>

      {/* Navegación principal con más separación superior */}
      <List sx={{ p: 2, pt: 3 }}>
        {navItems.map((item) => {
          const selected =
            item.to === '/patient/dashboard'
              ? location.pathname === item.to
              : location.pathname.startsWith(item.to);
          return (
            <ListItem key={item.to} disablePadding sx={{ mb: 0.4 }}>
              <ListItemButton
                component={RouterLink}
                to={item.to}
                sx={navItemStyles}
                selected={selected}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Sección inferior con enlace a Instagram y copyright */}
      <Box sx={{ mt: 'auto' }}>
        <Divider />
        <List sx={{ p: 2, pt: 1.5 }}>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              component="a"
              href="https://www.instagram.com/app.medifyhc"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...navItemStyles, mb: 0 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <InstagramIcon />
              </ListItemIcon>
              <ListItemText
                primary="Instagram"
                primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemText
              primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              primary={`© ${new Date().getFullYear()} MedifyHC`}
              sx={{ px: 1 }}
            />
          </ListItem>
        </List>
      </Box>
    </Box>
  );


  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw' }}>
      <CssBaseline />
      <SessionTimeout />

      {/* AppBar igual al MainLayout */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' }, px: { xs: 1, sm: 2 } }}>
          {/* Menú móvil */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: 'none' }, borderRadius: '12px' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo responsivo */}
          <RouterLink
            to={`${base}/dashboard`}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <img src={logo} alt="Logo Medify" style={{ height: '32px', marginRight: '8px' }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              noWrap
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              MEDIFY HEALTHCARE
            </Typography>
          </RouterLink>

          {/* Toggle tema claro/oscuro */}
          <Tooltip title={mode === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{ ml: 2, borderRadius: '12px' }}
              aria-label="toggle theme"
            >
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {/* Chip al perfil del paciente (igual patrón al MainLayout) */}
          {loadingProfile ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <RouterLink to={`${base}/profile`} style={{ textDecoration: 'none' }}>
              <Chip
                avatar={
                  <Avatar
                    src={profile?.foto_perfil || undefined}
                    alt={profile?.first_name || 'Paciente'}
                    sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 } }}
                  >
                    {(profile?.first_name || 'P').charAt(0).toUpperCase()}
                  </Avatar>
                }
                label={
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      maxWidth: { xs: 80, sm: 'none' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {`${profile?.first_name || 'Paciente'} ${profile?.last_name || ''}`.trim()}
                  </Typography>
                }
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  height: { xs: 32, sm: 'auto' },
                  borderRadius: '16px',
                  borderColor: 'common.white',
                  color: 'common.white',
                  '& .MuiChip-avatar': {
                    color: 'accent.main',
                    backgroundColor: 'common.white',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              />
            </RouterLink>
          )}

          {/* Logout: icono en móviles */}
          <Tooltip title="Cerrar Sesión">
            <IconButton
              color="inherit"
              onClick={handleLogout}
              sx={{
                ml: 1,
                borderRadius: '12px',
                display: { xs: 'flex', sm: 'none' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>

          {/* Logout: botón en pantallas medianas+ (mismos colores que MainLayout) */}
          <Button
            variant="contained"
            onClick={handleLogout}
            sx={{
              ml: 2,
              borderRadius: '20px',
              display: { xs: 'none', sm: 'flex' },
              bgcolor: 'accent.main',
              color: 'accent.contrastText',
              '&:hover': {
                bgcolor: 'accent.dark',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              },
            }}
          >
            Cerrar Sesión
          </Button>
        </Toolbar>
      </AppBar>

      {/* Drawer responsivo igual patrón al MainLayout */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Contenido principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1, width: '100%', overflowY: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default PatientLayout;
